import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import {
  EmptyState,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
  Spinner,
  formatDate,
} from '../components/ui';
import { ALL_STATUSES, type AudienceFilter, type CampaignPreview, type EmailCampaign, type EmailTemplate } from '../types';

/**
 * The campaign composer.
 *
 * The important thing on this screen is that you can see who a filter
 * actually catches, and what one of them will actually receive, before
 * anything is sent. Sending to 150 people is not undoable, so the preview is
 * not a nicety — it is the safety rail.
 */
export function EmailPage() {
  const { can } = useAuth();
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
  const [campaigns, setCampaigns] = useState<EmailCampaign[] | null>(null);
  const [audience, setAudience] = useState<AudienceFilter>({ payment: '' });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [sendError, setSendError] = useState<unknown>(null);
  const [sent, setSent] = useState<{ queued: number; unreachable: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  function loadCampaigns() {
    api
      .get<{ campaigns: EmailCampaign[] }>('/email/campaigns')
      .then((r) => setCampaigns(r.campaigns))
      .catch(() => undefined);
  }

  useEffect(() => {
    api
      .get<{ templates: EmailTemplate[] }>('/email/templates')
      .then((r) => setTemplates(r.templates))
      .catch(() => undefined);
    loadCampaigns();
  }, []);

  // Recount as the filters and wording change, so the number next to the
  // send button is never stale by the time it is pressed.
  useEffect(() => {
    if (subject.trim() === '' || body.trim() === '') {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setPreviewError(null);
      api
        .post<CampaignPreview>('/email/campaigns/preview', { audience, subject, body_html: body })
        .then((result) => {
          if (!cancelled) setPreview(result);
        })
        .catch((failure) => {
          if (!cancelled) {
            setPreview(null);
            setPreviewError(failure);
          }
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [audience, subject, body]);

  function useTemplate(code: string) {
    const template = templates?.find((t) => t.code === code);
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body_html);
    setConfirming(false);
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    setSendError(null);
    setBusy(true);
    try {
      const result = await api.post<{ queued: number; unreachable: number }>('/email/campaigns', {
        audience,
        subject,
        body_html: body,
      });
      setSent(result);
      setSubject('');
      setBody('');
      setPreview(null);
      setConfirming(false);
      loadCampaigns();
    } catch (failure) {
      setSendError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Email members</h1>
        <div className="head-actions">
          {can('email_schedule') ? (
            <Link to="/email/reminders" className="button-link">
              Automatic reminders
            </Link>
          ) : null}
          <Link to="/email/newsletters" className="button-link">
            Newsletters
          </Link>
          <Link to="/email/templates" className="button-link">
            Templates
          </Link>
          <Link to="/email/newsletter-images" className="button-link">
            Newsletter pictures
          </Link>
          <Link to="/email/log" className="button-link">
            Delivery log
          </Link>
        </div>
      </div>

      {sent !== null ? (
        <Notice kind="success">
          {sent.queued} email{sent.queued === 1 ? '' : 's'} queued.
          {sent.unreachable > 0
            ? ` ${sent.unreachable} member${sent.unreachable === 1 ? '' : 's'} could not be emailed and will need contacting another way.`
            : ''}{' '}
          They go out on the next send run. <Link to="/email/log">Watch the delivery log</Link>.
        </Notice>
      ) : null}

      <form className="card form wide" onSubmit={send}>
        <h2>Who it goes to</h2>
        <div className="grid">
          <SelectField
            label="Membership status"
            value={audience.status ?? ''}
            onChange={(e) => setAudience({ ...audience, status: e.target.value })}
          >
            <option value="">Anyone</option>
            {ALL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="This year's subscription"
            value={audience.payment ?? ''}
            onChange={(e) => setAudience({ ...audience, payment: e.target.value as AudienceFilter['payment'] })}
          >
            <option value="">Doesn't matter</option>
            <option value="owing">Renewed but still owing</option>
            <option value="settled">Paid up</option>
            <option value="not_renewed">Not renewed at all this year</option>
          </SelectField>
          <SelectField
            label="Mentors"
            value={audience.is_mentor ?? ''}
            onChange={(e) => setAudience({ ...audience, is_mentor: e.target.value })}
          >
            <option value="">Doesn't matter</option>
            <option value="1">Only mentors</option>
            <option value="0">Only non-mentors</option>
          </SelectField>
        </div>
        <p className="muted small">
          Members who have resigned or died are never included, whatever is chosen here.
        </p>

        <h2>What it says</h2>
        {templates !== null && templates.length > 0 ? (
          <div className="field">
            <label htmlFor="template-picker">Start from a template</label>
            <select
              id="template-picker"
              defaultValue=""
              onChange={(e) => {
                useTemplate(e.target.value);
                e.target.value = '';
              }}
            >
              <option value="">Write from scratch…</option>
              {templates.map((template) => (
                <option key={template.id} value={template.code}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <Field label="Subject" required value={subject} onChange={(e) => setSubject(e.target.value)} />
        <div className="field">
          <label htmlFor="campaign-body">Message</label>
          <textarea
            id="campaign-body"
            rows={10}
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <span className="hint">
            Simple HTML. Merge fields like <code>{'{{first_name}}'}</code> and{' '}
            <code>{'{{outstanding}}'}</code> are filled in per member —{' '}
            <Link to="/email/templates">the full list is on the templates page</Link>.
          </span>
        </div>

        <ErrorNotice error={previewError} />
        <ErrorNotice error={sendError} />

        {preview !== null ? (
          <div className="card preview-card">
            <h3>
              This will go to {preview.recipient_count} member
              {preview.recipient_count === 1 ? '' : 's'}
            </h3>
            {preview.via_guardian_count > 0 ? (
              <p className="muted small">
                {preview.via_guardian_count} of them via a parent or guardian's address.
              </p>
            ) : null}
            {Object.keys(preview.empty_fields).length > 0 ? (
              <Notice kind="error">
                {Object.entries(preview.empty_fields).map(([field, count]) => (
                  <span key={field} className="block">
                    <code>{`{{${field}}}`}</code> will be blank for {count} of{' '}
                    {preview.recipient_count} — reword that sentence, or narrow who it goes to.
                  </span>
                ))}
              </Notice>
            ) : null}

            {preview.unreachable_count > 0 ? (
              <Notice kind="info">
                {preview.unreachable_count} member
                {preview.unreachable_count === 1 ? ' has' : 's have'} no email address and will not
                get this: {preview.unreachable.map((m) => m.name).join(', ')}.
              </Notice>
            ) : null}

            {preview.sample !== null ? (
              <>
                <h3>What {preview.sample.name} will receive</h3>
                <div className="email-preview">
                  <p className="muted small">
                    To: {preview.sample.email}
                    <br />
                    Subject: <strong>{preview.sample.subject}</strong>
                  </p>
                  <hr />
                  <div dangerouslySetInnerHTML={{ __html: preview.sample.body_html }} />
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="actions">
          {!confirming ? (
            <button
              type="button"
              className="primary"
              disabled={preview === null || preview.recipient_count === 0}
              onClick={() => setConfirming(true)}
            >
              Send…
            </button>
          ) : (
            <>
              <button type="submit" className="primary" disabled={busy}>
                {busy
                  ? 'Queueing…'
                  : `Yes, send to ${preview?.recipient_count ?? 0} member${preview?.recipient_count === 1 ? '' : 's'}`}
              </button>
              <button type="button" onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </button>
            </>
          )}
        </div>
      </form>

      <div className="card">
        <h2>Sent before</h2>
        {campaigns === null ? <Spinner label="Loading" /> : null}
        {campaigns !== null && campaigns.length === 0 ? (
          <EmptyState title="Nothing sent yet" />
        ) : null}
        {campaigns !== null && campaigns.length > 0 ? (
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Subject</th>
                <th scope="col">Sent</th>
                <th scope="col">By</th>
                <th scope="col">Delivered</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td data-label="Subject">{campaign.subject}</td>
                  <td data-label="Sent">{formatDate(campaign.sent_at)}</td>
                  <td data-label="By">{campaign.created_by_name ?? '—'}</td>
                  <td data-label="Delivered">
                    {campaign.sent} of {campaign.recipients}
                    {campaign.failed > 0 ? (
                      <span className="muted block">{campaign.failed} failed</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </>
  );
}
